import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EyeOff } from "lucide-react";
import { useKanbanApi } from "@/utils/kanban-api";
import { KanbanStageSelector } from "@/components/freelancers/KanbanStageSelector";

interface FreelancerListKanbanStatusProps {
  vagaId: string;
  freelancerId: string;
  freelancerNome?: string;
  freelancerEmail?: string;
}

export function FreelancerListKanbanStatus({
  vagaId,
  freelancerId,
  freelancerNome,
  freelancerEmail,
}: FreelancerListKanbanStatusProps) {
  const kanbanApi = useKanbanApi();
  const queryClient = useQueryClient();

  // Mesma queryKey usada pelo KanbanStageSelector (Cards/Wide): reaproveita o
  // cache do React Query em vez de refazer o fetch por linha.
  const { data: cardsData = [], isLoading } = useQuery({
    queryKey: ["kanban-cards-all", vagaId],
    queryFn: () => kanbanApi.getCards(vagaId, true),
  });

  const freelancerCard = cardsData.find((card) => card.freelancer_id === freelancerId);
  const isDropInPlace = freelancerCard?.is_dropped && freelancerCard.drop_tipo === "in_place";

  const trigger = isLoading ? (
    <Badge variant="outline" className="text-muted-foreground">
      Carregando...
    </Badge>
  ) : isDropInPlace ? (
    <Badge
      variant="outline"
      className="max-w-[160px] gap-1 border-orange-300 bg-orange-50 text-orange-700"
    >
      <EyeOff className="h-3 w-3 shrink-0" />
      <span className="truncate">Descartado</span>
    </Badge>
  ) : freelancerCard ? (
    <Badge variant="outline" className="max-w-[160px] gap-1.5">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: freelancerCard.etapa?.cor || "#8B5CF6" }}
      />
      <span className="truncate">{freelancerCard.etapa?.nome || "Etapa desconhecida"}</span>
    </Badge>
  ) : (
    <Badge variant="outline" className="text-muted-foreground">
      Não adicionado
    </Badge>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer">
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <KanbanStageSelector
          vagaId={vagaId}
          freelancerId={freelancerId}
          freelancerNome={freelancerNome}
          freelancerEmail={freelancerEmail}
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: ["kanban-cards-all", vagaId] })
          }
        />
      </PopoverContent>
    </Popover>
  );
}
